/**
 * Pagination Component
 *
 * A comprehensive, accessible pagination component for the BiteWise application.
 * Supports various page navigation patterns and follows WCAG 2.1 AA guidelines.
 * Integrates with the existing design system and theme.
 *
 * @module components/ui/Pagination
 */

import React from 'react'
import { cn } from '../../utils/cn'
import { useTheme } from '../../hooks/useTheme'

/**
 * Pagination component props interface
 */
export interface PaginationProps {
  /** Current page number (1-based) */
  currentPage: number
  /** Total number of pages */
  totalPages: number
  /** Total number of items */
  totalItems: number
  /** Number of items per page */
  itemsPerPage: number
  /** Callback when page changes */
  onPageChange: (page: number) => void
  /** Whether pagination is disabled */
  disabled?: boolean
  /** Custom class name */
  className?: string
  /** Show page size info */
  showInfo?: boolean
  /** Show page numbers */
  showPageNumbers?: boolean
  /** Maximum number of page numbers to show */
  maxPageNumbers?: number
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Accessible Pagination component with theme support
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={2}
 *   totalPages={10}
 *   totalItems={95}
 *   itemsPerPage={10}
 *   onPageChange={(page) => setCurrentPage(page)}
 *   showInfo
 *   showPageNumbers
 * />
 * ```
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  disabled = false,
  className,
  showInfo = true,
  showPageNumbers = true,
  maxPageNumbers = 5,
  size = 'md'
}) => {
  const { isDark } = useTheme()

  // Don't render if there's only one page or no pages
  if (totalPages <= 1) {
    return null
  }

  // Calculate start and end item numbers for current page
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  // Calculate which page numbers to show
  const getVisiblePageNumbers = (): number[] => {
    if (totalPages <= maxPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const half = Math.floor(maxPageNumbers / 2)
    let start = Math.max(1, currentPage - half)
    const end = Math.min(totalPages, start + maxPageNumbers - 1)

    // Adjust start if we're near the end
    if (end - start + 1 < maxPageNumbers) {
      start = Math.max(1, end - maxPageNumbers + 1)
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  const visiblePageNumbers = getVisiblePageNumbers()

  // Handle page navigation
  const handlePrevious = () => {
    if (currentPage > 1 && !disabled) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages && !disabled) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number) => {
    if (page !== currentPage && !disabled) {
      onPageChange(page)
    }
  }

  // Size-based classes
  const sizeClasses = {
    sm: {
      container: 'px-3 py-2',
      button: 'px-2 py-1 text-xs',
      pageButton: 'px-3 py-1 text-xs',
      text: 'text-xs'
    },
    md: {
      container: 'px-4 py-3',
      button: 'px-2 py-2 text-sm',
      pageButton: 'px-4 py-2 text-sm',
      text: 'text-sm'
    },
    lg: {
      container: 'px-6 py-4',
      button: 'px-3 py-2 text-base',
      pageButton: 'px-5 py-2 text-base',
      text: 'text-base'
    }
  }

  // Theme-aware button classes
  const getButtonClasses = (isActive = false, isDisabled = false) => {
    const baseClasses = 'relative inline-flex items-center font-medium transition-all duration-200 focus:z-20 focus:outline-offset-0'

    if (isDisabled) {
      return cn(
        baseClasses,
        'opacity-50 cursor-not-allowed',
        isDark ? 'text-secondary-500' : 'text-secondary-400'
      )
    }

    if (isActive) {
      return cn(
        baseClasses,
        'z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        isDark
          ? 'bg-primary-600 text-white focus-visible:outline-primary-500'
          : 'bg-primary-600 text-white focus-visible:outline-primary-600'
      )
    }

    return cn(
      baseClasses,
      'ring-1 ring-inset focus:z-20',
      isDark
        ? 'text-secondary-100 ring-secondary-600 hover:bg-secondary-700 focus:outline-primary-500'
        : 'text-secondary-900 ring-secondary-300 hover:bg-secondary-50 focus:outline-primary-600'
    )
  }

  // Navigation arrow classes
  const getNavButtonClasses = (isDisabled = false) => {
    return cn(
      'relative inline-flex items-center font-medium transition-all duration-200',
      sizeClasses[size].button,
      isDisabled
        ? 'opacity-50 cursor-not-allowed'
        : cn(
            'ring-1 ring-inset hover:bg-opacity-75 focus:z-20 focus:outline-offset-0',
            isDark
              ? 'text-secondary-300 bg-secondary-800 ring-secondary-600 hover:bg-secondary-700 focus:outline-primary-500'
              : 'text-secondary-700 bg-white ring-secondary-300 hover:bg-secondary-50 focus:outline-primary-600'
          )
    )
  }

  return (
    <div className={cn(
      'flex items-center justify-between border',
      sizeClasses[size].container,
      'rounded-lg',
      isDark
        ? 'border-secondary-400 bg-secondary-800'
        : 'border-secondary-200 bg-white',
      disabled && 'opacity-60 pointer-events-none',
      className
    )}>
      {/* Mobile view - Previous/Next only */}
      <div className="flex flex-1 justify-between gap-4 sm:hidden">
        <button
          onClick={handlePrevious}
          disabled={currentPage <= 1 || disabled}
          className={cn(
            'rounded-md border',
            getNavButtonClasses(currentPage <= 1 || disabled),
            isDark ? 'border-secondary-400' : 'border-secondary-200'
          )}
          aria-label="Go to previous page"
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage >= totalPages || disabled}
          className={cn(
            'rounded-md border',
            getNavButtonClasses(currentPage >= totalPages || disabled),
            isDark ? 'border-secondary-600' : 'border-secondary-300'
          )}
          aria-label="Go to next page"
        >
          Next
        </button>
      </div>

      {/* Desktop view */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {/* Results info */}
        {showInfo && (
          <div className="mr-8">
            <p className={cn(
              sizeClasses[size].text,
              isDark ? 'text-secondary-300' : 'text-secondary-700'
            )}>
              Showing{' '}
              <span className="font-medium">{startItem}</span>
              {' '}to{' '}
              <span className="font-medium">{endItem}</span>
              {' '}of{' '}
              <span className="font-medium">{totalItems}</span>
              {' '}results
            </p>
          </div>
        )}

        {/* Page navigation */}
        <div className="ml-8">
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            {/* Previous button */}
            <button
              onClick={handlePrevious}
              disabled={currentPage <= 1 || disabled}
              className={cn(
                getNavButtonClasses(currentPage <= 1 || disabled),
                'rounded-l-md ring-1 ring-inset',
                isDark ? 'ring-secondary-600' : 'ring-secondary-300'
              )}
              aria-label="Go to previous page"
            >
              <span className="sr-only">Previous</span>
              <span aria-hidden="true">‹</span>
            </button>

            {/* Page numbers */}
            {showPageNumbers && visiblePageNumbers.map((pageNum) => {
              const isCurrentPage = pageNum === currentPage
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  disabled={disabled}
                  className={cn(
                    getButtonClasses(isCurrentPage, disabled),
                    sizeClasses[size].pageButton,
                    'ring-1 ring-inset',
                    isDark ? 'ring-secondary-600' : 'ring-secondary-300'
                  )}
                  aria-label={`Go to page ${pageNum}`}
                  aria-current={isCurrentPage ? 'page' : undefined}
                >
                  {pageNum}
                </button>
              )
            })}

            {/* Next button */}
            <button
              onClick={handleNext}
              disabled={currentPage >= totalPages || disabled}
              className={cn(
                getNavButtonClasses(currentPage >= totalPages || disabled),
                'rounded-r-md ring-1 ring-inset',
                isDark ? 'ring-secondary-600' : 'ring-secondary-300'
              )}
              aria-label="Go to next page"
            >
              <span className="sr-only">Next</span>
              <span aria-hidden="true">›</span>
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
}

export default Pagination